import React from 'react';
import styled from 'styled-components';
import { CaretDown, Pause, Play } from '@phosphor-icons/react';
import { colors } from '@config/colors';
import { borderRadius, spacing } from '@config/spacing';
import { typography } from '@config/typography';

/*
 * Where "See More" was: the page plays itself, and this pauses it — or
 * plays it again from wherever the reader is.
 */
export const AutoplayControl: React.FC<{ playing: boolean; onToggle: () => void }> = ({
  playing,
  onToggle,
}) => (
  <Button
    type="button"
    data-autoplay-control
    onClick={onToggle}
    aria-label={playing ? 'Pause the page' : 'Play the page'}
  >
    {playing ? <Pause weight="fill" aria-hidden="true" /> : <Play weight="fill" aria-hidden="true" />}
    <span>{playing ? 'Pause' : 'Play'}</span>
  </Button>
);

/*
 * Where the page's rhythm ends (June, 2026-09-23: at the Download section,
 * "stop the 'Play' and just have a See Blogs link"), the control's place
 * holds the way on instead: a press brings the next section up. It keeps
 * the control's mark, so what measures the control's room still finds it.
 */
export const NextSectionControl: React.FC<{ label: string; onGo: () => void }> = ({ label, onGo }) => (
  <Button type="button" data-autoplay-control onClick={onGo}>
    <span>{label}</span>
    <CaretDown weight="bold" aria-hidden="true" />
  </Button>
);

const Button = styled.button`
  position: fixed;
  left: 50%;
  bottom: ${spacing.lg};
  transform: translateX(-50%);
  z-index: 40;
  display: inline-flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.lg};
  border-radius: ${borderRadius.full};
  border: 1px solid rgba(255, 255, 255, 0.8);
  background: rgba(255, 255, 255, 0.55);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.1);
  cursor: pointer;

  font-family: ${typography.fontFamily.small};
  font-size: ${typography.fontSize.xs};
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${colors.highlight.dark};

  transition:
    background 0.2s ease,
    transform 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.85);
  }

  &:focus-visible {
    outline: 2px solid ${colors.highlight.dark};
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
  }

  @media print {
    display: none;
  }
`;
